// src/components/ai/copilot/QuickCoachNotesBox.js
import React from 'react';
import { MAX_NOTES_LENGTH } from '../../../utils/instructorCopilot';

/**
 * Minimal inline coach notes textarea.
 * The single-textarea entry point for the copilot workflow on surfaces where
 * the full InstructorQuickAiDraftInput would add too much UI weight.
 *
 * Props:
 *   value        — string — controlled value
 *   onChange     — (value: string) => void
 *   onGenerate   — () => void — called when generate button is clicked
 *   loading      — bool
 *   placeholder  — string
 *   label        — string — shown above textarea
 *   generateLabel — string — button text
 *   disabled     — bool
 */
function QuickCoachNotesBox({
  value = '',
  onChange,
  onGenerate,
  loading = false,
  placeholder = 'Quick coaching notes — rough thoughts, key observations, focus areas…',
  label = 'Add coach context (optional)',
  generateLabel = 'Generate Draft',
  disabled = false,
}) {
  const isDisabled = disabled || loading;
  const charCount = (value || '').length;
  const nearLimit = charCount > MAX_NOTES_LENGTH * 0.8;

  return (
    <div className="space-y-2">
      {label ? (
        <p className="text-xs font-semibold text-slate-500">{label}</p>
      ) : null}
      <textarea
        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:bg-slate-50 disabled:text-slate-400"
        rows={3}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={isDisabled}
        maxLength={MAX_NOTES_LENGTH}
      />
      <div className="flex items-center justify-between gap-3">
        <span className={`text-xs ${nearLimit ? 'text-amber-500' : 'text-slate-400'}`}>
          {charCount}/{MAX_NOTES_LENGTH}
        </span>
        <button
          type="button"
          onClick={isDisabled ? undefined : onGenerate}
          disabled={isDisabled}
          className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <svg aria-hidden="true" className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Thinking…
            </>
          ) : (
            <>
              <svg aria-hidden="true" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m3.343-5.657-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              {generateLabel}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default QuickCoachNotesBox;
